import {
  useEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn.js";

/**
 * Modal dialog primitive. Centered card over a backdrop with
 * Escape-to-close + outside-click-to-close. The consumer owns the
 * open/closed state via the `open` prop and the `onClose` callback
 * fires on user dismiss intents (Escape, backdrop click, explicit
 * Cancel buttons inside the modal body).
 *
 * Mobile pattern: on narrow viewports the modal becomes a full-width
 * card pinned with a 16px gutter. Long content scrolls within the
 * card — the page below stays fixed.
 *
 * For confirmation flows (export, delete, etc.), use `<Modal>` as
 * the shell and compose buttons + body inside.
 */
export type ModalProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  className,
  "aria-label": ariaLabel,
  ...rest
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Escape-to-close. The handler is bound to document only while the
  // modal is open so background routes' shortcuts keep working when
  // it isn't.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={(e) => {
        // Backdrop click closes; clicks inside the card bubble up
        // through `cardRef` and short-circuit here.
        if (cardRef.current !== null && cardRef.current.contains(e.target as Node)) {
          return;
        }
        onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      {...rest}
    >
      <div
        ref={cardRef}
        className={cn(
          "w-full max-w-lg rounded-lg bg-elevated border border-line shadow-lg",
          "max-h-[calc(100vh-2rem)] overflow-y-auto",
          className,
        )}
      >
        {title !== undefined ? (
          <div className="px-5 py-4 border-b border-line">
            <div className="text-lg font-semibold text-fg">{title}</div>
          </div>
        ) : null}
        <div className="px-5 py-4">{children}</div>
        {footer !== undefined ? (
          <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-3 border-t border-line">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
